import { ICurrency } from '../../features/Currency/types'
import { Journey, UnassignedConsignment, UnassignedLoad, UnassignedLoadContents } from '../../features/Journey/types'
import { ILoadTemplate } from '../../features/LoadTemplate/types'
import { ShipmentDto } from '../../features/Orders/NewShipment/types'
import { IService } from '../../features/Service/types'
import { ICarrier } from '../../features/Shipments/types'
import { IUpdateJourney } from '../../features/Transport/types'
import { IJourneyLegLoad } from './../../features/Transport/types'
import { apiSlice } from './apiQuery'

export const apiOrders = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getUnassignedLoads: build.query<IJourneyLegLoad[], { tenantId: number }>({
      query: (params) => ({
        url: `tenant/${params.tenantId}/loads/unassigned`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: (result, error, { tenantId }) => [{ type: 'Loads', tenantId }],
    }),
    getUnassignedConsignments: build.query<UnassignedConsignment[], { tenantId: number }>({
      query: (params) => ({
        url: `tenant/${params.tenantId}/loads/unassigned`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: (result, error, { tenantId }) => [{ type: 'Loads', tenantId }],
    }),
    getUnassignedLoadContents: build.query<UnassignedLoadContents[], { tenantId: number; loadId: number }>({
      query: ({ tenantId, loadId }) => ({
        url: `tenant/${tenantId}/load/${loadId}/contents`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: (result, error, { tenantId, loadId }) => [{ type: 'LoadContents', tenantId, loadId }],
    }),
    getUnassignedConsignmentLoads: build.query<UnassignedLoad[], { tenantId: number; shipmentBookingId: number }>({
      query: ({ tenantId, shipmentBookingId }) => ({
        url: `tenant/${tenantId}/consignment/${shipmentBookingId}/unassigned-loads`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: (result, error, { tenantId, shipmentBookingId }) => [{ type: 'ConsignmentLoads', tenantId, shipmentBookingId }],
    }),
    getJourneyLoadsById:build.query<IJourneyLegLoad[], { tenantId: number; journeyId: number }>({
      query: ({ tenantId, journeyId }) => {
        return {
          url: `/tenant/${tenantId}/journey/${journeyId}/loads`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: (result, error, tenantId) => [
        { type: 'Unassigned Loads', tenantId },
        { type: 'Journey', id: 'Journey' },
      ],
    }),
    getCurrenciesByCustomerId: build.query<
      ICurrency[],
      { customerId: number; hideDisabled: boolean }
    >({
      query: ({ customerId, hideDisabled }) => {
        return {
          url: `/customer/${customerId}/currency`,
          method: 'GET',
          domain: 'Core',
          params: { includeDisabled: !hideDisabled },
        }
      },
      providesTags: (result, error) => [{ type: 'Currency', id: 'Currency' }],
    }),
    getCurrencies: build.query<
      ICurrency[],
      { tenantId?: number; customerId?: number; hideDisabled: boolean }
    >({
      query: ({ tenantId, customerId, hideDisabled }) => {
        const url = customerId ? `/customer/${customerId}/currency` : `/tenant/${tenantId}/currency`
        return {
          url,
          method: 'GET',
          domain: 'Core',
          params: { includeDisabled: !hideDisabled },
        }
      },
      providesTags: (result, error) => [{ type: 'Currency', id: 'Currency' }],
    }),
    getCurrenciesByTenantId: build.query<ICurrency[], { tenantId: number; hideDisabled: boolean }>({
      query: ({ tenantId, hideDisabled }) => {
        return {
          url: `/tenant/${tenantId}/currency`,
          method: 'GET',
          domain: 'Core',
          params: { includeDisabled: !hideDisabled },
        }
      },
      providesTags: (result, error) => [{ type: 'Currency', id: 'Currency' }],
    }),
    deleteCurrencyById: build.mutation<any, { currencyId: number }>({
      query: ({ currencyId }) => {
        return {
          url: `/currency/${currencyId}`,
          method: 'DELETE',
          domain: 'Core',
        }
      },
      invalidatesTags: (result, error, { currencyId }) => [{ type: 'Currency', id: 'Currency' }],
    }),
    addCurrency: build.mutation<any, { body: ICurrency }>({
      query: ({ body }) => {
        return {
          url: '/currency',
          method: 'POST',
          domain: 'Core',
          body,
        }
      },
      invalidatesTags: (result, error, { body }) => [{ type: 'Currency', id: 'Currency' }],
    }),
    updateCurrency: build.mutation<any, { body: ICurrency }>({
      query: ({ body }) => {
        return {
          url: '/currency',
          method: 'PUT',
          domain: 'Core',
          body,
        }
      },
      invalidatesTags: (result, error, { body }) => [{ type: 'Currency', id: 'Currency' }],
    }),
    getJourneysByTenantId: build.query<Journey[], { tenantId: number }>({
      query: ({ tenantId }) => {
        return {
          url: `/tenant/${tenantId}/journey`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: (result, error) => [
        { type: 'Journey', id: 'Journey' },
        { type: 'Journeys', id: 'Journeys' },
      ],
    }),
    getJourneyByTenantId: build.query<IUpdateJourney, { id: number; tenantId: number }>({
      query: ({ id, tenantId }) => {
        return {
          url: `/tenant/${tenantId}/journey/${id}`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: (result, error) => [{ type: 'Journey', id: 'Journey' }],
    }),
    updateTransportJourney: build.mutation<
      any,
      { tenantId: number; journeyId: number; body: IUpdateJourney }
    >({
      query: ({ tenantId, journeyId, body }) => ({
        url: `/tenant/${tenantId}/journey/${journeyId}`,
        method: 'PUT',
        domain: 'Shipment',
        body: body,
      }),
      invalidatesTags: (journeyId) => [{ type: 'Journey', id: 'Journey' }],
    }),
    approveJourney: build.mutation<any, { tenantId: number; journeyId: number }>({
      query: ({ tenantId, journeyId }) => ({
        url: `/tenant/${tenantId}/journey/${journeyId}/approve`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: (result, error, { journeyId, tenantId }) => [
        { type: 'Journey', id: journeyId },
        { type: 'Transport Jobs', id: 'Transport Jobs' },
        { type: 'Journeys', id: tenantId },
        { type: 'Journey', id: 'Journey' },
      ],
    }),
    getCarriers: build.query<ICarrier[], { tenantId: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => {
        if (customerId) {
          return {
            url: `/customer/${customerId}/carrier`,
            method: 'GET',
            domain: 'Shipment',
          }
        }
        return {
          url: `/tenant/${tenantId}/carrier`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: () => [{ type: 'Carrier', id: 'Carrier' }],
    }),
    createCarrier: build.mutation<number, { tenantId: number; body: any }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/carrier`,
        method: 'POST',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: () => [{ type: 'Carrier', id: 'Carrier' }],
    }),
    updateCarrier: build.mutation<number, { tenantId: number; body: any }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/carrier/${body.id}`,
        method: 'PUT',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: () => [{ type: 'Carrier', id: 'Carrier' }],
    }),
    deleteCarrier: build.mutation<void, { tenantId: number; id: number }>({
      query: ({ tenantId, id }) => ({
        url: `/tenant/${tenantId}/carrier/${id}`,
        method: 'DELETE',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [{ type: 'Carrier', id: 'Carrier' }],
    }),
    saveDraftJourney: build.mutation<any, { tenantId: number; body: any }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/journey`,
        method: 'POST',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: (result, error, tenantId) => [{ type: 'Journeys', tenantId }],
    }),
    updateJourney: build.mutation<any, { tenantId: number; journeyId: number; body: any }>({
      query: ({ tenantId, journeyId, body }) => ({
        url: `/tenant/${tenantId}/journey/${journeyId}`,
        method: 'PUT',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: (result, error, tenantId) => [{ type: 'Journeys', tenantId }],
    }),
    deleteJourney: build.mutation<void, { tenantId: number; journeyId: number }>({
      query: ({ tenantId, journeyId }) => ({
        url: `/tenant/${tenantId}/journey/${journeyId}`,
        method: 'DELETE',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Journey', id: 'Journey' },
        { type: 'Journeys', id: 'Journeys' },
      ],
    }),
    createShipment:build.mutation<any, { shipment: ShipmentDto; tenantId: any; customerId?: any }>(
      {
        query: ({ shipment, tenantId, customerId }) => {
          return {
            url: customerId
              ? `/customer/${customerId}/order/shipment`
              : `/tenant/${tenantId}/order/shipment`,
            method: 'POST',
            domain: 'Shipment',
            body: shipment,
          }
        },
        invalidatesTags: () => [{ type: 'Orders' }, { type: 'Shipments' }],
      }
    ),
    updateShipment: build.mutation<any, { shipment: ShipmentDto; tenantId: any; customerId?: any }>(
      {
        query: ({ shipment, tenantId, customerId }) => {
          return {
            url: customerId
              ? `/customer/${customerId}/order/${shipment.id}/shipment`
              : `/tenant/${tenantId}/order/${shipment.id}/shipment`,
            method: 'PUT',
            domain: 'Shipment',
            body: shipment,
          }
        },
        invalidatesTags: (result, error, { shipment }) => [
          { type: 'Shipments', id: 'Shipments' },
          { type: 'Shipments', id: shipment.id },
          { type: 'Orders', id: 'Orders' },
        ],
      }
    ),
    getShipmentById: build.query<
      any,
      { tenantId?: number; customerId?: number; shipmentId: number }
    >({
      query: ({ tenantId, customerId, shipmentId }) => {
        return {
          url: customerId
            ? `/customer/${customerId}/order/${shipmentId}/shipment`
            : `/tenant/${tenantId}/order/${shipmentId}/shipment`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: (result, error, { shipmentId }) => [
        { type: 'Shipments', id: 'Shipments' },
        { type: 'Shipments', id: shipmentId },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    getShipmentSummaries: build.query<
      any,
      {
        tenantId?: number
        customerId?: number
        includeUnsubmitted?: boolean
        includeAbandoned?: boolean
        includeCompleted?: boolean
      }
    >({
      query: ({ tenantId, customerId, includeUnsubmitted, includeAbandoned, includeCompleted }) => {
        const params = new URLSearchParams()
        if (includeUnsubmitted !== undefined) {
          params.append('includeUnsubmitted', includeUnsubmitted.toString())
        }
        if (includeAbandoned !== undefined) {
          params.append('includeAbandoned', includeAbandoned.toString())
        }
        if (includeCompleted !== undefined) {
          params.append('includeCompleted', includeCompleted.toString())
        }

        const queryString = params.toString()
        const url = customerId
          ? `/customer/${customerId}/order/shipment/summary`
          : `/tenant/${tenantId}/order/shipment/summary`

        return {
          url: queryString ? `${url}?${queryString}` : url,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: () => [
        { type: 'Shipments', id: 'Shipments' },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    getAssetHireSummary: build.query<any, { tenantId?: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => {
        return {
          url: customerId
            ? `/customer/${customerId}/order/asset-hire/summary`
            : `/tenant/${tenantId}/order/asset-hire/summary`,
          method: 'GET',
          domain: 'Shipment',
        }
      },
      providesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    recallOrder: build.mutation<any, { tenantId?: number; id: number; customerId?: number }>({
      query: ({ tenantId, id, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/recall`
          : `/tenant/${tenantId}/order/${id}/recall`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    rejectOrderByTenantId: build.mutation<any, { tenantId: number; id: number }>({
      query: ({ tenantId, id }) => ({
        url: `/tenant/${tenantId}/order/${id}/reject`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    resetReviewOrderByTenantId: build.mutation<any, { tenantId: number; id: number }>({
      query: ({ tenantId, id }) => ({
        url: `/tenant/${tenantId}/order/${id}/reset-review`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    reviewOrderByTenantId: build.mutation<any, { tenantId: number; id: number }>({
      query: ({ tenantId, id }) => ({
        url: `/tenant/${tenantId}/order/${id}/review`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    submitOrder: build.mutation<any, { tenantId?: number; id: number; customerId?: number }>({
      query: ({ tenantId, id, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/submit`
          : `/tenant/${tenantId}/order/${id}/submit`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    createDraftAssetHire: build.mutation<any, { tenantId: number; body: any; customerId?: number }>(
      {
        query: ({ tenantId, body, customerId }) => ({
          url: customerId
            ? `/customer/${customerId}/order/asset-hire`
            : `/tenant/${tenantId}/order/asset-hire`,
          method: 'POST',
          domain: 'Shipment',
          body,
        }),
        invalidatesTags: () => [
          { type: 'AssetHires', id: 'AssetHires' },
          { type: 'Orders', id: 'Orders' },
        ],
      }
    ),
    getAssetHires: build.query<any, { tenantId: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/asset-hire`
          : `/tenant/${tenantId}/order/asset-hire`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: () => [
        { type: 'AssetHires', id: 'AssetHires' },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    getAssetHireById: build.query<any, { tenantId: number; id: number; customerId?: number }>({
      query: ({ tenantId, id, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/asset-hire`
          : `/tenant/${tenantId}/order/${id}/asset-hire`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: () => [
        { type: 'AssetHire', id: 'AssetHire' },
        { type: 'AssetHires', id: 'AssetHires' },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    updateAssetHireById: build.mutation<
      any,
      { tenantId: number; id: number; body: any; customerId?: number }
    >({
      query: ({ tenantId, id, body, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/asset-hire`
          : `/tenant/${tenantId}/order/${id}/asset-hire`,
        method: 'PUT',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: () => [
        { type: 'AssetHire', id: 'AssetHire' },
        { type: 'AssetHires', id: 'AssetHires' },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    getAssetHireSummaries: build.query<any, { tenantId: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/order/asset-hire/summary`
          : `/tenant/${tenantId}/order/asset-hire/summary`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: () => [
        { type: 'AssetHireSummaries', id: 'AssetHireSummaries' },
        { type: 'AssetHires' },
      ],
    }),
    createService: build.mutation<any, { tenantId: number; body: IService }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/service`,
        method: 'POST',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: () => [{ type: 'Service', id: 'Service' }],
    }),
    editService: build.mutation<any, { tenantId: number; body: IService }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/service/${body.id}`,
        method: 'PUT',
        domain: 'Shipment',
        body,
      }),
      invalidatesTags: () => [{ type: 'Service', id: 'Service' }],
    }),
    deleteService: build.mutation<any, { tenantId: number; body: { id: number } }>({
      query: ({ tenantId, body }) => ({
        url: `/tenant/${tenantId}/service/${body.id}`,
        method: 'DELETE',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [{ type: 'Service', id: 'Service' }],
    }),
    getServiceById: build.query<any, { tenantId: number; id: number }>({
      query: ({ tenantId, id }) => ({
        url: `/tenant/${tenantId}/service/${id}`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: () => [{ type: 'Service', id: 'Service' }],
    }),
    getServices: build.query<any, { tenantId: number }>({
      query: ({ tenantId }) => ({
        url: `/tenant/${tenantId}/service`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: () => [{ type: 'Service', id: 'Service' }],
    }),
    getAllLoadTypes: build.query<any, void>({
      query: () => ({
        url: '/get-all-load-types',
        method: 'GET',
        domain: 'Shipment',
      }),
    }),
    getAllLoadTemplates: build.query<ILoadTemplate[], { customerId?: number }>({
      query: ({ customerId }) => ({
        url: `customer/${customerId}/load-template`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'LoadTemplate' as const, id })),
              { type: 'LoadTemplate', id: 'LIST' },
            ]
          : [{ type: 'LoadTemplate', id: 'LIST' }],
    }),
    acceptOrderByTenantId: build.mutation<any, { tenantId: number; id: number }>({
      query: (params) => ({
        url: `/tenant/${params.tenantId}/order/${params.id}/accept`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: (result, error, { tenantId }) => [
        { type: 'Loads', tenantId },
        { type: 'Orders', id: 'Orders' },
      ],
    }),
    getLoadTemplates: build.query<ILoadTemplate[], { tenantId: number; customerId?: number }>({
      query: ({ tenantId, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/load-template`
          : `/tenant/${tenantId}/load-template`,
        method: 'GET',
        domain: 'Shipment',
      }),
      providesTags: ['LoadTemplate'],
    }),
    getLoadTemplateById: build.query<
      ILoadTemplate,
      { id: number; tenantId: number; customerId?: number }
    >({
      query: ({ id, tenantId, customerId }) => ({
        url: customerId
          ? `/customer/${customerId}/load-template/${id}`
          : `/tenant/${tenantId}/load-template/${id}`,
        method: 'GET',
        domain: 'Shipment',
      }),
    }),
    deleteLoadTemplate: build.mutation<void, { id: number; customerId?: number }>({
      query: ({ id, customerId }) => ({
        url: `customer/${customerId}/load-template/${id}`,
        method: 'DELETE',
        domain: 'Shipment',
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'LoadTemplate', id }],
    }),
    createLoadTemplate: build.mutation<ILoadTemplate, { body: ILoadTemplate; customerId?: number }>(
      {
        query: ({ body, customerId }) => ({
          url: `customer/${customerId}/load-template`,
          method: 'POST',
          domain: 'Shipment',
          body,
        }),
        invalidatesTags: [{ type: 'LoadTemplate', id: 'LIST' }],
      }
    ),
    updateLoadTemplate: build.mutation<ILoadTemplate, { body: ILoadTemplate; customerId?: number }>(
      {
        query: ({ body, customerId }) => ({
          url: `customer/${customerId}/load-template`,
          method: 'PUT',
          domain: 'Shipment',
          body,
        }),
        invalidatesTags: (result, error, { body }) => [{ type: 'LoadTemplate', id: body.id }],
      }
    ),
    getOrderCrn: build.query<any, { customerId: number; id: number; crn: string }>({
      query: ({ customerId, id, crn }) => ({
        url: `/customer/${customerId}/order/${id ?? 0}/CRN/${crn}`,
        method: 'GET',
        domain: 'Shipment',
      }),
    }),
    checkCrnExistsForCustomerShipmentBooking: build.query<
      boolean,
      { customerId: number; id: number; reference: string }
    >({
      query: ({ customerId, id, reference }) => ({
        url: `/customer/${customerId}/shipment-booking/${id}/ref/${reference}`,
        method: 'GET',
        domain: 'Shipment',
      }),
    }),
    abandonOrder: build.mutation<any, { tenantId?: number; customerId?: number; id: number }>({
      query: ({ tenantId, customerId, id }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/abandon`
          : `/tenant/${tenantId}/order/${id}/abandon`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    createOrder: build.mutation<any, { tenantId?: number; customerId?: number; id: number }>({
      query: ({ tenantId, customerId, id }) => ({
        url: customerId
          ? `/customer/${customerId}/order/${id}/create`
          : `/tenant/${tenantId}/order/${id}/create`,
        method: 'PUT',
        domain: 'Shipment',
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Orders', id: 'Orders' },
        { type: 'Shipments', id: 'Shipments' },
        { type: 'Shipments', id: id },
        { type: 'AssetHires', id: 'AssetHires' },
      ],
    }),
    copyShipment: build.mutation<any, { tenantId?: number; customerId?: number; id: number }>({
      query: ({ tenantId, customerId, id }) => ({
        url: customerId
          ? `/customer/${customerId}/order/shipment/copy`
          : `/tenant/${tenantId}/order/shipment/copy`,
        method: 'POST',
        domain: 'Shipment',
        body: { id },
      }),
      invalidatesTags: () => [
        { type: 'Orders', id: 'Orders' },
        { type: 'Shipments', id: 'Shipments' },
      ],
    }),
  }),
})

export const {
  useAcceptOrderByTenantIdMutation,
  useAddCurrencyMutation,
  useApproveJourneyMutation,
  useCreateCarrierMutation,
  useCreateDraftAssetHireMutation,
  useCreateLoadTemplateMutation,
  useCreateServiceMutation,
  useCreateShipmentMutation,
  useCopyShipmentMutation,
  useDeleteCarrierMutation,
  useDeleteCurrencyByIdMutation,
  useDeleteJourneyMutation,
  useDeleteLoadTemplateMutation,
  useDeleteServiceMutation,
  useEditServiceMutation,
  useGetAllLoadTemplatesQuery,
  useGetAllLoadTypesQuery,
  useGetAssetHireByIdQuery,
  useGetAssetHiresQuery,
  useGetAssetHireSummariesQuery,
  useGetAssetHireSummaryQuery,
  useGetCarriersQuery,
  useGetCurrenciesByCustomerIdQuery,
  useGetCurrenciesByTenantIdQuery,
  useGetCurrenciesQuery,
  useGetJourneyByTenantIdQuery,
  useGetJourneyLoadsByIdQuery,
  useGetJourneysByTenantIdQuery,
  useGetLoadTemplatesQuery,
  useGetLoadTemplateByIdQuery,
  useGetServiceByIdQuery,
  useGetServicesQuery,
  useGetShipmentByIdQuery,
  useGetShipmentSummariesQuery,
  useGetUnassignedLoadsQuery,
  useGetUnassignedConsignmentsQuery,
  useGetUnassignedLoadContentsQuery,
    useGetUnassignedConsignmentLoadsQuery,
    useLazyGetUnassignedConsignmentLoadsQuery,
    useRecallOrderMutation,
  useRejectOrderByTenantIdMutation,
  useResetReviewOrderByTenantIdMutation,
  useReviewOrderByTenantIdMutation,
  useSaveDraftJourneyMutation,
  useSubmitOrderMutation,
  useUpdateAssetHireByIdMutation,
  useUpdateCarrierMutation,
  useUpdateCurrencyMutation,
  useUpdateJourneyMutation,
  useUpdateLoadTemplateMutation,
  useUpdateShipmentMutation,
  useUpdateTransportJourneyMutation,
  useGetOrderCrnQuery,
  useCheckCrnExistsForCustomerShipmentBookingQuery,
  useAbandonOrderMutation,
  useCreateOrderMutation,
} = apiOrders
